import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopHeader from "./TopHeader";
import Chatbot from "../common/Chatbot";
import authService from "../../services/authService";
import api from "../../services/api";
import {
  CACHE_KEYS,
  readCachedValue,
  writeCachedValue,
} from "../../utils/localCache";
import "./DashboardLayout.css";

const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;

const DashboardLayout = () => {
  const [user, setUser] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem("worksphere_sidebar_collapsed") === "true",
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/profile/me/", {
          params: { minimal: true },
        });
        const profileData = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        if (profileData) {
          setUser(profileData);
          localStorage.setItem("user", JSON.stringify(profileData));
          writeCachedValue(CACHE_KEYS.minimalProfile, profileData);
        }
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    };

    const isAuthenticated = authService.isAuthenticated();
    
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    const currentUser = authService.getCurrentUser();
    const cachedProfile = readCachedValue(
      CACHE_KEYS.minimalProfile,
      PROFILE_CACHE_TTL_MS,
    );

    const initialUser = cachedProfile || currentUser;
    if (initialUser) {
      setUser(initialUser);
      const hasRequiredFields =
        initialUser.role !== undefined &&
        initialUser.is_verified !== undefined;

      if (!cachedProfile || !hasRequiredFields) {
        fetchProfile();
      }
    } else {
      // Authenticated but no user profile stored yet (e.g. from PIN login)
      fetchProfile();
    }
  }, [navigate]);

  // Handle redirection for unverified users
  useEffect(() => {
    if (user) {
      const role = (user.role?.toLowerCase() || "").replace(/\s+/g, "_");
      const isStaff =
        [
          "it_admin",
          "ceo",
          "reporting_manager",
          "hr",
          "project_manager",
        ].includes(role) || user.is_staff;

      const isProfilePage = location.pathname === "/profile";

      if (!user.is_verified && !isStaff && !isProfilePage) {
        console.warn(
          "Unverified user attempting to access restricted page. Redirecting to profile.",
        );
        navigate("/profile");
      }
    }
  }, [user, navigate, location.pathname]);

  const handleLogout = () => {
    setShowLogoutModal(false);
    authService.logout();
    navigate("/login");
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const toggleSidebarCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("worksphere_sidebar_collapsed", newState);
  };

  if (!user)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        Loading...
      </div>
    );

  return (
    <div className="dashboard-container">
      {/* Logout Confirmation Modal */}
      <div className={`confirm-modal ${showLogoutModal ? "active" : ""}`}>
        <div className="confirm-dialog">
          <div className="confirm-icon">
            <i className="bi bi-box-arrow-right"></i>
          </div>
          <h3 className="confirm-title">Logout Confirmation</h3>
          <p className="confirm-message">
            Are you sure you want to logout from your account?
          </p>
          <div className="confirm-buttons">
            <button
              className="btn-confirm btn-confirm-cancel"
              onClick={() => setShowLogoutModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn-confirm btn-confirm-logout"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <Sidebar
        user={user}
        isMobileOpen={isMobileOpen}
        isCollapsed={isCollapsed}
        toggleCollapse={toggleSidebarCollapse}
        closeMobileSidebar={() => setIsMobileOpen(false)}
        setShowLogoutModal={setShowLogoutModal}
      />

      <main className={`main-content ${isCollapsed ? "collapsed" : ""}`}>
        {/* Desktop Header */}
        <TopHeader user={user} />

        <div className="content-wrapper">
          {/* Mobile Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 d-lg-none">
            <button
              className="btn btn-outline-secondary"
              onClick={toggleMobileSidebar}
            >
              <i className="bi bi-list"></i> Menu
            </button>
            <span className="fw-bold">WorkSphere AI</span>
            <div
              className="avatar-circle bg-primary text-white"
              style={{ width: 32, height: 32 }}
            >
              {user.first_name ? user.first_name[0] : "U"}
            </div>
          </div>

          <Outlet context={{ user }} />
        </div>
      </main>

      {/* AI Chatbot Widget */}
      <Chatbot />

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="drawer-overlay active d-lg-none"
          onClick={() => setIsMobileOpen(false)}
          style={{ zIndex: 999 }}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;
